
import { useStore } from '@/store'
import {
    ChipOption,
    computed,
    defineComponent,
    PropType,
    ref,
    onMounted,
    onBeforeUnmount,
    StyleValue
} from 'vue'
import { abbreviateNumber } from '@/utils'
import { useI18n } from 'vue-i18n';

export default defineComponent({
    name: 'GoodRoadBetBox',
    emits: ['place-bet'],
    props: {
        betlabel: {
            type: String,
            default: ''
        },
        betRatio: {
            type: String,
            default: ''
        },
        color: {
            type: String as PropType<'red' | 'green' | 'blue' | 'none'>,
            default: 'none'
        },
        totalBet: {
            type: Number,
            default: 0
        },
        tempBet: {
            type: Number,
            default: 0
        },
        width: {
            type: Number,
            default: 20
        },
        height: {
            type: Number,
            default: 20
        },
        betType: {
            type: Number,
            default: 0
        },
        confirmedBet: {
            type: Number,
            default: 0
        },
        mWidth: {
            type: Number as PropType<number>,
            required: true
        },
        mHeight: {
            type: Number as PropType<number>,
            required: true
        },
    },
    setup(props, { emit }) {
        const store = useStore()
        const { t } = useI18n()
        const rootEl = ref<HTMLElement | null>(null)
        const labelEl = ref<HTMLElement | null>(null)
        const containerW = ref<number>(props.width)
        const containerH = ref<number>(props.height)
        const labelH = ref<number>(0)

        const chipsUI = ref<
            {
                x: number
                y: number
                amount: number
                class: string
                type: 'temp' | 'confirmed'
            }[]
        >([])

        const resetBetUI = () => {
            chipsUI.value = []
        }

        const removeTempUIBet = () => {
            chipsUI.value = chipsUI.value.filter((i) => i.type !== 'temp')
        }

        const applyTempUIBet = () => {
            chipsUI.value = chipsUI.value.map((i) => {
                if (i.type === 'temp') i.type = 'confirmed'
                return i
            })
        }

        const hasChips = (): boolean => chipsUI.value.length > 0

        const getChipsTotal = (): number =>
            chipsUI.value.reduce((sum, chip) => sum + (chip.amount || 0), 0)

        const toShortValue = (value: number) => {
            return abbreviateNumber(value)
        }

        const handleOnPlaceChip = (arg: {
            t: number
            amt: number
            type: 'temp' | 'confirmed'
        }) => {
            if (arg.t !== props.betType) return

            if (
                arg.type === 'confirmed' &&
                chipsUI.value.length === 1 &&
                chipsUI.value[0].amount === arg.amt
            ) {
                return
            } else if (arg.type === 'confirmed') {
                chipsUI.value = []
            }

            let w = 0
            let h = 0
            let chipSize = 0

            if (deviceOrientation.value === 'landscape') {
                w = props.width
                h = props.height
                chipSize = 20
            } else {
                w = props.mWidth
                h = props.mHeight
                chipSize = 24
            }

            const chipHalf = chipSize / 2

            let startY = Math.floor(h / 2)
            let startX = Math.floor(w / 2)

            if (chipsUI.value.length > 0) {
                const lastIndex = chipsUI.value.length - 1
                const lastItem = chipsUI.value[lastIndex]
                startY = lastItem.y
                startX = lastItem.x
            }

            // get x
            // let _x = getRandomPosition(chipHalf, w)
            let _x = getRandomPosition(chipHalf, w - chipHalf)
            while (Math.abs(_x - startX) > chipSize) {
                _x = getRandomPosition(chipHalf, w)

                if (_x + chipSize > w) {
                    const offset = Math.abs(_x + chipSize - w)
                    _x = _x - offset
                }
            }

            // get y
            // let _y = getRandomPosition(chipHalf, h)
            let _y = getRandomPosition(chipHalf, h - chipHalf)
            while (Math.abs(_y - startY) > chipSize) {
                _y = getRandomPosition(chipHalf, h)

                if (_y + chipSize > h) {
                    const offset = Math.abs(_y + chipSize - h)
                    _y = _y - offset
                }
            }

            let _chipIndex = chips.value.value.indexOf(arg.amt)
            if (_chipIndex === -1) _chipIndex = 22

            chipsUI.value.push({
                x: _x,
                y: _y,
                amount: arg.amt,
                class: `_chip_${_chipIndex}`,
                type: arg.type
            })
        }

        const placeBet = () => {
            emit('place-bet', props.betType)
        }

        const sumOfTempBetandConfirmedBet = computed(() => {
            const confirmedBet = props.confirmedBet ?? 0
            const tempBet = props.tempBet ?? 0
            return confirmedBet + tempBet
        })

        const getRandomPosition = (min: number, max: number) => {
            return Math.floor(Math.random() * (max - min + 1) + min)
        }

        const getChipPositionStyle = (post: number): string => {
            const _var =
                deviceOrientation.value === 'landscape'
                    ? 'var(--pc1)'
                    : 'var(--mp1)'
            return `calc(${_var}*${post})`
        }

        const updateContainerSize = () => {
            if (rootEl.value) {
                const totalW = Math.floor(rootEl.value.clientWidth)
                const totalH = Math.floor(rootEl.value.clientHeight)
                labelH.value = labelEl.value
                    ? Math.floor(labelEl.value.clientHeight)
                    : 0

                // leave a small padding below the label so chips don't overlap it
                const usableH = Math.max(20, totalH - labelH.value - 6)

                containerW.value = Math.max(20, totalW)
                containerH.value = usableH
            }
        }

        onMounted(() => {
            updateContainerSize()
            requestAnimationFrame(updateContainerSize)
            window.addEventListener('resize', updateContainerSize)
        })

        onBeforeUnmount(() => {
            window.removeEventListener('resize', updateContainerSize)
        })

        const chipContainerStyle = computed((): StyleValue | undefined => {
            if (props.width > 0 && props.height > 0) {
                const w =
                    deviceOrientation.value === 'landscape'
                        ? Math.abs(props.width)
                        : Math.abs(props.mWidth)
                const h =
                    deviceOrientation.value === 'landscape'
                        ? Math.abs(props.height)
                        : Math.abs(props.mHeight)

                const _var =
                    deviceOrientation.value === 'landscape'
                        ? 'var(--pc1)'
                        : 'var(--mp1)'
                return {
                    width: `calc(${_var}*${w})`,
                    height: `calc(${_var}*${h})`,
                    // backgroundColor: 'red'
                }
            }
            return undefined
        })

        const deviceOrientation = computed(
            (): 'landscape' | 'portrait' => store.getters['deviceOrientation']
        )
        const chips = computed((): ChipOption => store.getters['chips'])
        return {
            rootEl,
            chipsUI,
            chipContainerStyle,
            resetBetUI,
            removeTempUIBet,
            applyTempUIBet,
            hasChips,
            getChipsTotal,
            handleOnPlaceChip,
            sumOfTempBetandConfirmedBet,
            placeBet,
            toShortValue,
            getChipPositionStyle,
            t
        }
    }
})
